// src/components/Navbar.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { performLogout } from  '../../store/authSlice';
import { selectUserPreferences, updatePreference, saveUserPreferences } from '../../store/preferencesSlice';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { AppDispatch } from '../../store';
import NotificationCenter from '../notifications/NotificationCenter';
import ErrorIndicator from '../shared/ErrorIndicator';
import VersionModal from '../shared/VersionModal';
import { useTranslation } from '../../locales/useTranslation';
import { useNewGui } from '../../hooks/useNewGui';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { FiChevronDown, FiMonitor } from 'react-icons/fi';
import RolePreviewControl from './RolePreviewControl';
import { useRolePreviewOptional } from '../../contexts/RolePreviewContext';

const Navbar: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const preferences = useSelector(selectUserPreferences);
  const useNew = useNewGui();
  const { settings: systemSettings } = useSystemSettings();
  const rolePreview = useRolePreviewOptional();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslation();

  // Get the image path from the environment variable
  const logoPath = process.env.REACT_APP_PMA_LOGO;

  // Generate breadcrumbs based on current route
  const generateBreadcrumbs = (pathname: string) => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs: Array<{ label: string; path?: string; isActive?: boolean }> = [];

    // Always start with Home/Dashboard
    breadcrumbs.push({ label: useNew ? 'Home' : t.systemDashboard, path: '/' });

    // Add hierarchical breadcrumbs based on route
    if (pathname === '/' || pathname === '') {
      breadcrumbs[0].isActive = true;
    } else if (pathname.startsWith('/licmgmt')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: t.breadcrumbLicenses, isActive: true });
    } else if (pathname.startsWith('/uniqueusers')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: t.breadcrumbInfraUsers, isActive: true });
    } else if (pathname.startsWith('/user-table')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: t.breadcrumbInfraUsers }, { label: 'server accts', isActive: true });
    } else if (pathname.startsWith('/user/')) {
      const userId = params.email || segments[1] || 'Unknown';
      breadcrumbs.push(
        { label: t.breadcrumbAdmin },
        { label: t.breadcrumbInfraUser, path: '/uniqueusers' }, // ← FIXED: Added path for clickability
        { label: userId, isActive: true }
      );
    } else if (pathname.startsWith('/gen-users')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: 'Infra Users Generation', isActive: true });
    } else if (pathname.startsWith('/extension-approvals')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: 'Extension Approvals', isActive: true });
    } else if (pathname.startsWith('/audit')) {
      breadcrumbs.push({ label: t.breadcrumbAdmin }, { label: 'Audit Logs', isActive: true });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs(location.pathname);


  const dispatch = useDispatch<AppDispatch>(); // Use the typed dispatch
  const handleLogout = async () => {
    toggleDropdown();

    // CRITICAL SECURITY: Trigger immediate cross-tab logout invalidation
    const logoutTimestamp = Date.now().toString();
    localStorage.setItem('force_logout_timestamp', logoutTimestamp);

    // Broadcast to other tabs (modern browsers)
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('auth_channel');
      channel.postMessage({ type: 'LOGOUT', timestamp: logoutTimestamp });
      // Close channel after sending message
      setTimeout(() => channel.close(), 100);
    }

    console.log('🚨 Logout initiated - invalidating all tabs');

    await dispatch(performLogout()); // Now dispatch expects async actions
    navigate('/login');
  };

//   const toggleDropdown = () => {
//     setDropdownOpen(!dropdownOpen);
//   };
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // Always-visible GUI switch — doesn't rely on the user finding the dropdown
  // (previously the only entry point) or on the notifications bell, which is
  // hidden independently via its own preference and shares no state with this.
  const guiRolledOut = systemSettings.NEW_GUI_ENABLED === 'true';
  const handleToggleGui = async () => {
    const value = !preferences.useNewGui;
    const updatedPreferences = { ...preferences, useNewGui: value };
    dispatch(updatePreference({ key: 'useNewGui', value }));
    try {
      await dispatch(saveUserPreferences(updatedPreferences)).unwrap();
    } catch (error) {
      console.error('Failed to save GUI preference:', error);
      dispatch(updatePreference({ key: 'useNewGui', value: !value }));
    }
  };

  // Close dropdown on clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false); // Close dropdown when clicking outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside); // For mobile touch support

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);


  const isLoggedIn = !!user;
  const pmaTitle = (process.env.REACT_APP_PMA_TITLE || '').trim() || 'Platform Mgmt App';

  if (useNew) {
    const initial = user?.email ? user.email.charAt(0).toUpperCase() : '?';
    return (
      <nav className="font-plex bg-gradient-to-r from-accent to-accent-hover h-16 px-5 flex items-center gap-8 sticky top-0 z-40 shadow-md">
        {/* Left: real product identity — sized to lead the page, not hide in a corner */}
        <div className="flex items-center gap-3 flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
          {logoPath && logoPath !== "" ? (
            <img src={`/${logoPath}`} alt="Home" className="h-8 w-auto" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-white/15 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {pmaTitle.slice(0, 1)}
            </div>
          )}
          <span className="text-lg font-bold text-white hidden sm:inline whitespace-nowrap tracking-tight">{pmaTitle}</span>
        </div>

        <div className="w-px h-6 bg-white/20 flex-shrink-0 hidden md:block" />

        {/* Breadcrumbs — still secondary to the product identity above, but readable */}
        <nav className="flex items-center gap-2 text-sm min-w-0 flex-1 overflow-hidden">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-white/40 flex-shrink-0">/</span>}
              {crumb.path && !crumb.isActive ? (
                <Link to={crumb.path} className="text-white/80 hover:text-white transition-colors truncate">
                  {crumb.label}
                </Link>
              ) : (
                <span className={`truncate ${crumb.isActive ? 'text-white font-semibold' : 'text-white/80'}`}>
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Right: notifications + user menu with real button affordance */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {user ? (
            <>
              {preferences.showErrorConsole && <ErrorIndicator />}
              {preferences.showNotifications && <NotificationCenter />}
              {guiRolledOut && (
                <button
                  onClick={handleToggleGui}
                  title={preferences.useNewGui ? 'Switch back to the classic console' : 'Try the redesigned console'}
                  className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/25 hover:border-white/50 hover:bg-white/10 transition-colors text-xs font-medium text-white"
                >
                  <FiMonitor className="text-xs" />
                  {preferences.useNewGui ? 'Classic GUI' : 'Try new GUI'}
                </button>
              )}
              <RolePreviewControl />
              <div className="relative">
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-white/25 hover:border-white/50 hover:bg-white/10 transition-colors"
                >
                  <span className="w-7 h-7 rounded-full bg-white/20 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {initial}
                  </span>
                  <span className="text-sm text-white max-w-[160px] truncate">{user.email}</span>
                  {rolePreview?.isPreviewing && (
                    <span className="hidden lg:inline text-[10px] uppercase tracking-wide bg-amber-400/90 text-amber-950 px-1.5 py-0.5 rounded">
                      {rolePreview.previewRole}
                    </span>
                  )}
                  <FiChevronDown className="text-white/75 text-xs flex-shrink-0" />
                </button>
                {dropdownOpen && (
                  <div ref={dropdownRef} className="absolute right-0 mt-2 w-48 bg-surface border border-border-subtle shadow-md rounded z-30">
                    <button className="block px-4 py-2 text-ink-primary hover:bg-surface-raised w-full text-left text-sm" onClick={() => navigate('/user/preferences')}>
                      Preferences
                    </button>
                    <button className="block px-4 py-2 text-ink-primary hover:bg-surface-raised w-full text-left text-sm" onClick={() => navigate('/user/notifications')}>
                      Notifications
                    </button>
                    <button className="block px-4 py-2 text-ink-primary hover:bg-surface-raised w-full text-left truncate text-sm" onClick={() => navigate('/my-profile')} style={{ maxWidth: '100%' }}>
                      {user.email}
                    </button>
                    <button className="block px-4 py-2 text-ink-primary hover:bg-surface-raised w-full text-left text-sm" onClick={() => { setVersionModalOpen(true); setDropdownOpen(false); }}>
                      Deployment version
                    </button>
                    <button className="block px-4 py-2 text-danger hover:bg-danger/5 w-full text-left text-sm" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
              <VersionModal open={versionModalOpen} onClose={() => setVersionModalOpen(false)} />
            </>
          ) : (
            <div className="w-40 opacity-0 pointer-events-none" aria-hidden="true"></div>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-blue-800 p-4 flex items-center relative sticky top-0 z-40 shadow-lg">

      {/* Left side - Logo/Home */}
      <div className="flex items-center flex-shrink-0">
        <div
          className="flex items-center cursor-pointer mr-6"
          onClick={() => navigate('/')}
        >
          {logoPath && logoPath !== "" ? (
            <img
              src={`/${logoPath}`}
              alt="Home"
              className="h-8 w-auto"
            />
          ) : (
            <span className="text-white text-xl font-bold">Home</span>
          )}
        </div>
      </div>

      {/* Center - Title and Breadcrumbs - adaptive padding */}
      <div className={`flex-1 text-center ${isLoggedIn ? 'px-64' : 'px-8'}`}>
        {/* Title - top row */}
        <div className="text-white text-3xl font-bold">
          {pmaTitle}
        </div>
        {/* Breadcrumbs - bottom row */}
        <div className="text-gray-300 text-sm mt-1">
          <nav className="flex items-center justify-center">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                {crumb.path && !crumb.isActive ? (
                  <Link
                    to={crumb.path}
                    className="hover:text-blue-200 transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={`${
                    crumb.isActive
                      ? 'text-blue-200 font-medium'
                      : 'text-gray-300'
                  }`}>
                    {crumb.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>

      {/* Right side - User content or placeholder for consistent spacing */}
      <div className="text-white relative flex items-center gap-4 flex-shrink-0">
        {user ? (
          <>
            {/* Error Indicator - conditionally shown based on preference */}
            {preferences.showErrorConsole && <ErrorIndicator />}

            {/* Notification Center - conditionally shown based on preference */}
            {preferences.showNotifications && <NotificationCenter />}

            {/* GUI switch - always visible once rolled out org-wide; independent of
                the notifications bell above, which has its own separate preference */}
            {guiRolledOut && (
              <button
                onClick={handleToggleGui}
                title={preferences.useNewGui ? 'Switch back to the classic console' : 'Try the redesigned console'}
                className="hidden md:inline-flex items-center px-2.5 py-1 rounded border border-white/30 hover:bg-blue-700 transition-colors text-xs font-medium text-white"
              >
                {preferences.useNewGui ? 'Classic GUI' : 'Try new GUI'}
              </button>
            )}

            <RolePreviewControl />

            {/* User Dropdown */}
            <div className="flex items-center">
              <button
                onClick={toggleDropdown}
                className="mt-2 w-48 shadow-lg text-white-700 hover:bg-blue-400 rounded-md z-20"
              >
                <span className="material-icons">{user.email}</span>
              </button>
            {dropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-md z-30"
              >
                <button
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                  onClick={() => navigate('/user/preferences')}
                >
                  Preferences
                </button>
                <button
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                  onClick={() => navigate('/user/notifications')}
                >
                  Notifications
                </button>
                <button
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left truncate"
                  onClick={() => navigate('/my-profile')}
                  style={{ maxWidth: '100%' }}
                >
                  {user.email}
                </button>
                <button
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                  onClick={() => { setVersionModalOpen(true); setDropdownOpen(false); }}
                >
                  Deployment version
                </button>
                <button
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            )}
            </div>

            <VersionModal open={versionModalOpen} onClose={() => setVersionModalOpen(false)} />
          </>
        ) : (
          /* Invisible placeholder to maintain consistent spacing on login screen */
          <div className="w-48 opacity-0 pointer-events-none" aria-hidden="true"></div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
